import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../config';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';

const FILTER_STYLES = {
  warm: 'hue-rotate(15deg) saturate(1.3) brightness(0.95)',
  cool: 'hue-rotate(200deg) saturate(1.2) brightness(0.9)',
  golden: 'sepia(0.5) saturate(1.4) brightness(1.05)',
  mystic: 'hue-rotate(270deg) saturate(1.5) brightness(0.85) contrast(1.1)',
  neon: 'invert(0.1) hue-rotate(90deg) saturate(2) brightness(1.1)',
  none: 'none'
};

const FILTER_LABELS = {
  warm: 'Calido', cool: 'Frio', golden: 'Dorado',
  mystic: 'Mistico', neon: 'Neon', none: 'Sin filtro'
};

const CUALjuegoComponent = ({ user }) => {
  const [tab, setTab] = useState('profile');
  const [profile, setProfile] = useState(null);
  const [levelInfo, setLevelInfo] = useState(null);
  const [roles, setRoles] = useState({});
  const [allRoles, setAllRoles] = useState({});
  const [metrics, setMetrics] = useState({});
  const [skillTree, setSkillTree] = useState({});
  const [unlockedSkills, setUnlockedSkills] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [worldMode, setWorldMode] = useState('real');
  const [myLoc, setMyLoc] = useState({ lat: 10.4806, lng: -66.9036 });
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({ avatar_name: '', avatar_bio: '', avatar_powers: [] });
  const [powerInput, setPowerInput] = useState('');
  const [showCreateRole, setShowCreateRole] = useState(false);
  const [roleForm, setRoleForm] = useState({ name: '', description: '', icon: 'fa-star', color: '#6b7280', visible: true });
  const [availableIcons, setAvailableIcons] = useState([]);
  const [availableColors, setAvailableColors] = useState([]);
  const [editingRole, setEditingRole] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [profRes, skillRes, achRes, lbRes, rolesRes] = await Promise.all([
        axios.get(`${API}/cualjuego/profile`),
        axios.get(`${API}/cualjuego/skills`),
        axios.get(`${API}/cualjuego/achievements`),
        axios.get(`${API}/cualjuego/leaderboard`),
        axios.get(`${API}/cualjuego/roles`)
      ]);
      setProfile(profRes.data.profile);
      setLevelInfo(profRes.data.level);
      setRoles(profRes.data.roles);
      setAllRoles(profRes.data.all_roles);
      setMetrics(profRes.data.metrics);
      setWorldMode(profRes.data.profile?.world_mode || 'real');
      setSkillTree(skillRes.data.skill_tree);
      setUnlockedSkills(skillRes.data.unlocked_skills);
      setAchievements(achRes.data.achievements);
      setLeaderboard(lbRes.data.leaderboard);
      setAvailableIcons(rolesRes.data.available_icons || []);
      setAvailableColors(rolesRes.data.available_colors || []);

      if (profRes.data.profile) {
        setEditForm({
          avatar_name: profRes.data.profile.avatar_name || '',
          avatar_bio: profRes.data.profile.avatar_bio || '',
          avatar_powers: profRes.data.profile.avatar_powers || []
        });
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const toggleWorld = async (mode) => {
    try {
      await axios.post(`${API}/cualjuego/world/toggle`, { mode });
      setWorldMode(mode);
      fetchAll();
    } catch (err) { console.error(err); }
  };

  const updateFilter = async (filter) => {
    try {
      await axios.post(`${API}/cualjuego/world/settings`, { filter });
      setProfile(prev => ({ ...prev, symworld_filter: filter }));
    } catch (err) { console.error(err); }
  };

  const unlockSkill = async (skillId) => {
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/cualjuego/skills/unlock`, { skill_id: skillId });
      alert(res.data.message);
      fetchAll();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
    finally { setProcessing(false); }
  };

  const saveProfile = async () => {
    setProcessing(true);
    try {
      await axios.post(`${API}/cualjuego/profile/update`, editForm);
      setEditMode(false);
      fetchAll();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
    finally { setProcessing(false); }
  };

  const addPower = () => {
    if (powerInput.trim() && editForm.avatar_powers.length < 5) {
      setEditForm(prev => ({ ...prev, avatar_powers: [...prev.avatar_powers, powerInput.trim()] }));
      setPowerInput('');
    }
  };

  const removePower = (idx) => {
    setEditForm(prev => ({ ...prev, avatar_powers: prev.avatar_powers.filter((_, i) => i !== idx) }));
  };

  const setRole = async (roleId) => {
    try {
      await axios.post(`${API}/cualjuego/profile/update`, { primary_role: roleId });
      fetchAll();
    } catch (err) { console.error(err); }
  };

  const createRole = async () => {
    if (!roleForm.name.trim()) return;
    setProcessing(true);
    try {
      await axios.post(`${API}/cualjuego/roles`, roleForm);
      setShowCreateRole(false);
      setRoleForm({ name: '', description: '', icon: 'fa-star', color: '#6b7280', visible: true });
      fetchAll();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
    finally { setProcessing(false); }
  };

  const updateRole = async (roleId) => {
    setProcessing(true);
    try {
      await axios.put(`${API}/cualjuego/roles/${roleId}`, editingRole);
      setEditingRole(null);
      fetchAll();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
    finally { setProcessing(false); }
  };

  const deleteRole = async (roleId, roleName) => {
    if (!window.confirm(`Eliminar rol "${roleName}"?`)) return;
    try {
      await axios.delete(`${API}/cualjuego/roles/${roleId}`);
      fetchAll();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  if (loading) return <div className="p-8 text-center text-gray-400">Cargando CUALjuego...</div>;

  const primaryRole = allRoles[profile?.primary_role] || allRoles.explorador || {};
  const isSymworld = worldMode === 'symworld';
  const filterCSS = isSymworld ? FILTER_STYLES[profile?.symworld_filter || 'warm'] : 'none';

  return (
    <div className="space-y-5" data-testid="cualjuego-module">
      {/* World Toggle */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">CUALjuego</h2>
          <p className="text-sm text-gray-500">Tu realidad, tu mundo, sin limites</p>
        </div>
        <div className="flex items-center gap-2" data-testid="world-toggle">
          <button onClick={() => toggleWorld('real')} data-testid="world-real-btn"
            className={`px-4 py-2 rounded-l-xl text-sm font-medium transition-all ${!isSymworld ? 'bg-green-600 text-white shadow-lg' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            <i className="fa fa-globe mr-1"></i>Mundo Real
          </button>
          <button onClick={() => toggleWorld('symworld')} data-testid="world-sym-btn"
            className={`px-4 py-2 rounded-r-xl text-sm font-medium transition-all ${isSymworld ? 'bg-purple-600 text-white shadow-lg' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            <i className="fa fa-dragon mr-1"></i>CUALsymworld
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        {['profile', 'map', 'skills', 'achievements', 'leaderboard'].map(t => (
          <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === t ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {t === 'profile' ? 'Perfil RPG' : t === 'map' ? 'Mapa' : t === 'skills' ? 'Habilidades' : t === 'achievements' ? `Logros (${achievements.filter(a => a.unlocked).length}/${achievements.length})` : 'Ranking'}
          </button>
        ))}
      </div>

      {/* === PROFILE TAB === */}
      {tab === 'profile' && profile && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Avatar Card */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-5 text-white relative overflow-hidden" data-testid="avatar-card">
              <div className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-10" style={{ background: primaryRole.color, filter: 'blur(40px)' }}></div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl" style={{ background: primaryRole.color + '22', color: primaryRole.color }}>
                  <i className={`fa ${primaryRole.icon || 'fa-user'}`}></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold">{profile.avatar_name}</h3>
                  <p className="text-xs opacity-70" style={{ color: primaryRole.color }}>{primaryRole.name || 'Explorador'}</p>
                </div>
              </div>

              {/* Level bar */}
              {levelInfo && (
                <div className="mb-4">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-bold">Nivel {levelInfo.level}</span>
                    <span className="opacity-60">{levelInfo.xp_in_level}/{levelInfo.xp_for_next} XP</span>
                  </div>
                  <div className="w-full h-2.5 bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${levelInfo.progress_pct}%`, background: `linear-gradient(90deg, ${primaryRole.color}, ${primaryRole.color}88)` }}></div>
                  </div>
                  <p className="text-xs opacity-40 mt-1">{levelInfo.current_xp} XP total</p>
                </div>
              )}

              {/* Bio */}
              {profile.avatar_bio && <p className="text-xs text-gray-400 mb-3 italic">"{profile.avatar_bio}"</p>}

              {/* Powers (CUALsymworld) */}
              {profile.avatar_powers?.length > 0 && (
                <div className="mb-3">
                  <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Poderes (CUALsymworld)</p>
                  <div className="flex flex-wrap gap-1">
                    {profile.avatar_powers.map((p, i) => (
                      <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-purple-900/50 text-purple-300 border border-purple-700/30">{p}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-white/5 rounded-lg p-2">
                  <p className="text-lg font-bold">{Object.keys(roles).length}</p>
                  <p className="text-[10px] opacity-50">Roles</p>
                </div>
                <div className="bg-white/5 rounded-lg p-2">
                  <p className="text-lg font-bold">{unlockedSkills.length}</p>
                  <p className="text-[10px] opacity-50">Skills</p>
                </div>
                <div className="bg-white/5 rounded-lg p-2">
                  <p className="text-lg font-bold">{achievements.filter(a => a.unlocked).length}</p>
                  <p className="text-[10px] opacity-50">Logros</p>
                </div>
              </div>

              <button onClick={() => setEditMode(!editMode)} data-testid="edit-avatar-btn"
                className="mt-3 w-full text-xs text-gray-400 hover:text-white py-1.5 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
                {editMode ? 'Cancelar' : 'Editar Avatar'}
              </button>
            </div>

            {/* Edit form */}
            {editMode && (
              <div className="mt-3 bg-white rounded-xl border border-gray-200 p-4 space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Nombre</label>
                  <input type="text" maxLength="50" value={editForm.avatar_name} onChange={e => setEditForm({...editForm, avatar_name: e.target.value})}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Bio</label>
                  <textarea maxLength="200" value={editForm.avatar_bio} onChange={e => setEditForm({...editForm, avatar_bio: e.target.value})}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm h-16 resize-none" placeholder="Tu historia en CUALjuego..." />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Poderes CUALsymworld (max 5)</label>
                  <div className="flex gap-1 flex-wrap mb-1">
                    {editForm.avatar_powers.map((p, i) => (
                      <span key={i} className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full flex items-center gap-1">
                        {p} <button onClick={() => removePower(i)} className="text-purple-400 hover:text-purple-800"><i className="fa fa-times text-[8px]"></i></button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-1">
                    <input type="text" value={powerInput} onChange={e => setPowerInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addPower())}
                      placeholder="Volar, Teletransportarse..." className="flex-1 px-2 py-1 border border-gray-300 rounded-lg text-xs" />
                    <button onClick={addPower} disabled={editForm.avatar_powers.length >= 5}
                      className="px-2 py-1 bg-purple-100 text-purple-700 rounded-lg text-xs disabled:opacity-50">+</button>
                  </div>
                </div>
                <button onClick={saveProfile} disabled={processing} data-testid="save-avatar-btn"
                  className="w-full bg-gray-900 text-white py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50">
                  {processing ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            )}
          </div>

          {/* Roles & Metrics */}
          <div className="lg:col-span-2 space-y-4">
            {/* Roles - System + Custom */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900">Mis Roles ({Object.keys(roles).length})</h3>
                <button onClick={() => setShowCreateRole(!showCreateRole)} data-testid="create-role-btn"
                  className="text-xs px-3 py-1.5 rounded-lg bg-gradient-to-r from-pink-500 to-purple-500 text-white font-medium hover:opacity-90 transition-opacity">
                  <i className="fa fa-plus mr-1"></i>{showCreateRole ? 'Cancelar' : 'Crear Rol'}
                </button>
              </div>

              {/* Create Role Form */}
              {showCreateRole && (
                <div className="mb-4 p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-200 space-y-3" data-testid="create-role-form">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Nombre del Rol</label>
                    <input type="text" maxLength="30" value={roleForm.name} onChange={e => setRoleForm({...roleForm, name: e.target.value})}
                      placeholder="Ej: Visionario, Estratega..." data-testid="role-name-input"
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-300 focus:border-purple-400" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Descripcion</label>
                    <input type="text" maxLength="100" value={roleForm.description} onChange={e => setRoleForm({...roleForm, description: e.target.value})}
                      placeholder="Define tu rol..." data-testid="role-desc-input"
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-300 focus:border-purple-400" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Icono</label>
                    <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto p-2 bg-white rounded-lg border border-gray-200">
                      {availableIcons.map(ic => (
                        <button key={ic} onClick={() => setRoleForm({...roleForm, icon: ic})} data-testid={`icon-${ic}`}
                          className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${roleForm.icon === ic ? 'bg-purple-600 text-white shadow-md scale-110' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`}>
                          <i className={`fa ${ic} text-sm`}></i>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Color</label>
                    <div className="flex flex-wrap gap-1.5">
                      {availableColors.map(c => (
                        <button key={c} onClick={() => setRoleForm({...roleForm, color: c})} data-testid={`color-${c}`}
                          className={`w-7 h-7 rounded-full transition-all ${roleForm.color === c ? 'ring-2 ring-offset-2 ring-purple-500 scale-110' : 'hover:scale-105'}`}
                          style={{ background: c }} />
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-600">Visible para otros:</label>
                    <button onClick={() => setRoleForm({...roleForm, visible: !roleForm.visible})} data-testid="role-visible-toggle"
                      className={`w-10 h-5 rounded-full transition-colors relative ${roleForm.visible ? 'bg-green-500' : 'bg-gray-300'}`}>
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${roleForm.visible ? 'left-5' : 'left-0.5'}`} />
                    </button>
                  </div>
                  <div className="flex items-center gap-3 pt-1">
                    <div className="flex items-center gap-2 flex-1 bg-white rounded-lg border border-gray-200 p-2">
                      <i className={`fa ${roleForm.icon} text-lg`} style={{ color: roleForm.color }}></i>
                      <div>
                        <p className="text-sm font-medium text-gray-800">{roleForm.name || 'Tu Rol'}</p>
                        <p className="text-[10px] text-gray-500">{roleForm.description || 'Descripcion'}</p>
                      </div>
                    </div>
                    <button onClick={createRole} disabled={processing || !roleForm.name.trim()} data-testid="submit-create-role"
                      className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50">
                      {processing ? '...' : 'Crear'}
                    </button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {Object.entries(allRoles).map(([rid, role]) => {
                  const unlocked = !!roles[rid];
                  const isPrimary = profile.primary_role === rid;
                  const isCustom = role.is_custom;
                  return (
                    <div key={rid} className={`relative p-3 rounded-xl text-center transition-all border ${
                      isPrimary ? 'border-2 shadow-md' : unlocked ? 'border-gray-200 hover:shadow-sm' : 'border-gray-100 opacity-40'
                    }`} style={isPrimary ? { borderColor: role.color, background: role.color + '08' } : {}} data-testid={`role-${rid}`}>
                      {isCustom && (
                        <div className="absolute top-1 right-1 flex gap-0.5">
                          {role.visible === false && <i className="fa fa-eye-slash text-[8px] text-gray-400" title="Oculto"></i>}
                          <button onClick={(e) => { e.stopPropagation(); deleteRole(rid, role.name); }} data-testid={`delete-role-${rid}`}
                            className="w-4 h-4 rounded-full bg-red-100 text-red-400 hover:bg-red-200 hover:text-red-600 flex items-center justify-center text-[8px] transition-colors">
                            <i className="fa fa-times"></i>
                          </button>
                        </div>
                      )}
                      <button onClick={() => unlocked && setRole(rid)} disabled={!unlocked} className="w-full">
                        <i className={`fa ${role.icon} text-xl mb-1 block`} style={{ color: unlocked ? role.color : '#d1d5db' }}></i>
                        <p className="text-xs font-medium text-gray-800">{role.name}</p>
                        {isCustom && <span className="text-[8px] px-1 py-0.5 rounded bg-pink-100 text-pink-600">custom</span>}
                        {isPrimary && <span className="text-[9px] px-1 py-0.5 rounded bg-gray-900 text-white ml-0.5">ACTIVO</span>}
                        {!unlocked && !isCustom && <span className="text-[9px] text-gray-400 block mt-0.5">Bloqueado</span>}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Activity Metrics */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Actividad Real</h3>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {[
                  { label: 'Transacciones', value: metrics.transactions, icon: 'fa-exchange-alt' },
                  { label: 'Ventas', value: metrics.marketplace_sales, icon: 'fa-tag' },
                  { label: 'Viajes', value: metrics.rides, icon: 'fa-car' },
                  { label: 'Escrows', value: metrics.escrows_created, icon: 'fa-lock' },
                  { label: 'Contratos', value: metrics.smart_contracts, icon: 'fa-file-contract' },
                  { label: 'Items', value: metrics.items_created, icon: 'fa-cube' },
                  { label: 'Automatics', value: metrics.automations, icon: 'fa-robot' },
                  { label: 'Volumen', value: `${metrics.total_volume} Cc`, icon: 'fa-coins' },
                ].map((m, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-2.5 text-center">
                    <i className={`fa ${m.icon} text-gray-400 text-sm mb-1 block`}></i>
                    <p className="text-base font-bold text-gray-900">{m.value}</p>
                    <p className="text-[10px] text-gray-500">{m.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* === MAP TAB === */}
      {tab === 'map' && (
        <div className="space-y-3">
          {/* Sym filter selector */}
          {isSymworld && (
            <div className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-xl px-4 py-2" data-testid="symworld-filters">
              <span className="text-xs font-medium text-purple-700">Filtro visual:</span>
              {Object.entries(FILTER_LABELS).map(([key, label]) => (
                <button key={key} onClick={() => updateFilter(key)} data-testid={`filter-${key}`}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                    profile?.symworld_filter === key ? 'bg-purple-600 text-white' : 'bg-white text-purple-600 hover:bg-purple-100'
                  }`}>{label}</button>
              ))}
            </div>
          )}

          <div className="rounded-xl overflow-hidden border-2" style={{ borderColor: isSymworld ? '#7c3aed' : '#10b981', height: '400px' }} data-testid="game-map">
            <div style={{ filter: filterCSS, height: '100%', transition: 'filter 0.5s ease' }}>
              <MapContainer center={[myLoc.lat, myLoc.lng]} zoom={13} style={{ height: '100%', width: '100%' }}>
                <TileLayer url="https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}" subdomains={['mt0','mt1','mt2','mt3']}
                  attribution="&copy; Google" />
                <Marker position={[myLoc.lat, myLoc.lng]}>
                  <Popup>
                    <div className="text-center">
                      <p className="font-bold">{profile?.avatar_name || user?.full_name}</p>
                      <p className="text-xs">{primaryRole.name} | Nivel {levelInfo?.level}</p>
                      <p className="text-xs text-gray-400">{isSymworld ? 'CUALsymworld' : 'Mundo Real'}</p>
                    </div>
                  </Popup>
                </Marker>
              </MapContainer>
            </div>
          </div>

          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>{isSymworld ? 'CUALsymworld - Tu imaginacion es el limite' : 'Mundo Real - Posicion verificada KYC'}</span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full" style={{ background: isSymworld ? '#7c3aed' : '#10b981' }}></span>
              {isSymworld ? 'Modo Simbiotico' : 'Modo Real'}
            </span>
          </div>
        </div>
      )}

      {/* === SKILLS TAB === */}
      {tab === 'skills' && (
        <div className="space-y-4" data-testid="skill-tree">
          {Object.entries(skillTree).map(([roleId, data]) => {
            const role = data.role;
            const roleUnlocked = !!roles[roleId];
            return (
              <div key={roleId} className={`bg-white rounded-xl border overflow-hidden ${roleUnlocked ? 'border-gray-200' : 'border-gray-100 opacity-50'}`}>
                <div className="px-5 py-3 flex items-center gap-2" style={{ background: roleUnlocked ? role.color + '10' : '#f9fafb' }}>
                  <i className={`fa ${role.icon}`} style={{ color: role.color }}></i>
                  <span className="text-sm font-semibold" style={{ color: roleUnlocked ? role.color : '#9ca3af' }}>{role.name}</span>
                  {!roleUnlocked && <span className="text-[10px] text-gray-400 ml-auto">Rol bloqueado</span>}
                </div>
                <div className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    {data.skills.map((skill, i) => (
                      <React.Fragment key={skill.id}>
                        <div className={`flex-1 rounded-xl p-3 border-2 transition-all ${
                          skill.unlocked ? 'border-green-300 bg-green-50' : roleUnlocked ? 'border-gray-200 bg-white hover:border-gray-300' : 'border-gray-100 bg-gray-50'
                        }`} data-testid={`skill-${skill.id}`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className={`text-xs font-bold ${skill.unlocked ? 'text-green-700' : 'text-gray-600'}`}>T{skill.tier}</span>
                            {skill.unlocked && <i className="fa fa-check-circle text-green-500 text-xs"></i>}
                          </div>
                          <p className="text-sm font-medium text-gray-900">{skill.name}</p>
                          <p className="text-[10px] text-gray-500 mt-0.5">{skill.benefit}</p>
                          <p className="text-[10px] text-gray-400 mt-1">{skill.xp_cost} XP</p>
                          {!skill.unlocked && roleUnlocked && (
                            <button onClick={() => unlockSkill(skill.id)} disabled={processing} data-testid={`unlock-${skill.id}`}
                              className="mt-2 w-full text-[10px] py-1 rounded bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50">
                              Desbloquear
                            </button>
                          )}
                        </div>
                        {i < data.skills.length - 1 && (
                          <div className="flex-shrink-0 w-6 flex items-center justify-center">
                            <i className="fa fa-arrow-right text-gray-300 text-xs"></i>
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* === ACHIEVEMENTS TAB === */}
      {tab === 'achievements' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="achievements-grid">
          {achievements.map(ach => (
            <div key={ach.id} data-testid={`achievement-${ach.id}`}
              className={`rounded-xl p-4 border-2 transition-all ${ach.unlocked ? 'border-amber-300 bg-amber-50' : 'border-gray-100 bg-gray-50 opacity-60'}`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${ach.unlocked ? 'bg-amber-200 text-amber-700' : 'bg-gray-200 text-gray-400'}`}>
                  <i className={`fa ${ach.icon} text-lg`}></i>
                </div>
                <div>
                  <p className={`text-sm font-semibold ${ach.unlocked ? 'text-amber-800' : 'text-gray-500'}`}>{ach.name}</p>
                  <p className="text-xs text-gray-500">{ach.description}</p>
                  <p className="text-[10px] text-amber-600 mt-0.5">+{ach.xp_reward} XP</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* === LEADERBOARD TAB === */}
      {tab === 'leaderboard' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="leaderboard">
          <div className="px-6 py-4 bg-gradient-to-r from-gray-900 to-gray-800 text-white">
            <h3 className="font-semibold">Ranking CUALjuego</h3>
          </div>
          {leaderboard.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm">Sin jugadores aun</div>
          ) : (
            <div className="divide-y divide-gray-50">
              {leaderboard.map(p => (
                <div key={p.rank} className={`px-6 py-3 flex items-center gap-4 ${p.is_me ? 'bg-indigo-50' : ''}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    p.rank === 1 ? 'bg-amber-100 text-amber-700' : p.rank === 2 ? 'bg-gray-200 text-gray-700' : p.rank === 3 ? 'bg-orange-100 text-orange-700' : 'bg-gray-50 text-gray-500'
                  }`}>
                    {p.rank <= 3 ? ['', '1st', '2nd', '3rd'][p.rank] : `#${p.rank}`}
                  </div>
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <i className={`fa ${p.role_icon}`} style={{ color: p.role_color }}></i>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{p.avatar_name} {p.is_me && <span className="text-xs text-indigo-600">(tu)</span>}</p>
                      <p className="text-xs text-gray-400">{p.role_name} | {p.roles_count} roles | {p.skills_count} skills</p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold text-gray-900">Nv.{p.level}</p>
                    <p className="text-xs text-gray-400">{p.total_xp} XP</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CUALjuegoComponent;
